<template>
  <div
    :class="[
      'match-card',
      { isSelected },
      { 'is-inactive': swapIsInactive },
      {
        'has-badge':
          (!swapIsInactive && expireDays) ||
          match.unreadCount > 0 ||
          match.isNew
      }
    ]"
  >
    <BaseImage
      :key="partBProposition.propositionId"
      :url="partBProposition.residences[0].image"
      :grey-bg="true"
      class="primary-image"
    />

    <div v-if="!swapIsInactive && expireDays" class="badge expire-match-badge">
      <BaseIcon icon-file="clock-orange" :width="12" :height="12" />
      {{ expireDays }}
    </div>

    <div v-else-if="match.unreadCount > 0" class="badge">
      <BaseIcon icon-file="unread-mail-blue" :width="12" :height="12" />
      {{ match.unreadCount }}
    </div>

    <div v-else-if="match.isNew" class="badge">
      <div class="new-match-dot"></div>
      {{ $t('matches_card_new_badge') }}
    </div>
    <div v-if="match.lastMessage" class="last-message">
      <template v-if="match.lastMessage?.senderName === 'SYSTEM'">
        <div class="name-and-date">
          {{ JSON.parse(match.lastMessage?.message).displayName }} –
          {{ customDate(match.lastMessage?.sentAt) }}
        </div>
        <div
          class="message message-system"
          v-html="
            truncateText(
              convertSystemChatMessageWithoutAddress(
                match.lastMessage?.message
              ),
              40
            )
          "
        ></div>
      </template>
      <template v-else>
        <div class="name-and-date">
          {{ match.lastMessage.senderName }} –
          {{ customDate(match.lastMessage.sentAt) }}
        </div>
        <div
          class="message"
          v-html="
            match.lastMessage.message
              ? truncateText(match.lastMessage.message, 40)
              : ''
          "
        ></div>
      </template>
    </div>
    <div v-else class="last-message">
      <div class="name-and-date">
        {{ customDate(match.createdAt) }}
      </div>
      <div class="message">{{ $t('matches_card_new_match_msg') }}</div>
    </div>
    <div class="residence-info-container">
      <div class="residence-info">
        <div class="adress-container">
          <div class="street-address">
            {{ partBProposition.residences[0].streetAddress }}
            <template v-if="partBProposition.residences.length > 1">
              <br />
              & {{ partBProposition.residences[1].streetAddress }}
            </template>
            <BaseIcon
              v-if="partBProposition.verified"
              icon-file="verified-white-check"
              :width="14"
              :height="14"
              class="verified-badge"
            />
          </div>
          <p class="locality">
            {{ partBProposition.residences[0].locality }}
            <template v-if="partBProposition.residences.length > 1">
              <br />
              & {{ partBProposition.residences[1].locality }}
            </template>
          </p>
        </div>
        <div class="residence-info-details">
          <span>
            {{ partBProposition.residences[0].rent.toLocaleString() }}
            {{ $t('general_currency_short') }} &#8729;&nbsp;
          </span>
          <span>
            {{ partBProposition.residences[0].rooms }}
            {{ $tc('general_rooms') }} &#8729;&nbsp;
          </span>
          <span>
            {{ partBProposition.residences[0].sqm }}
            {{ $t('general_sqm_short') }}
          </span>
        </div>
      </div>
      <div class="link-container">
        <RouterLink
          v-if="partBPropositionActive"
          :to="{
            query: {
              propositionPageId: partBProposition.propositionId,
              type:
                match.propositions.length === 3 ? 'triangleswap' : 'directswap'
            }
          }"
          class="go-to-proposition"
          @click.native.stop
          >{{ $t('matches_card_show_prop') }}
        </RouterLink>
      </div>
    </div>
    <div v-if="match.propositions.length === 3">
      <div class="part-c-divider">
        <div class="line"></div>
        <span class="text">
          {{ $t('matches_card_part_c_divider_text') }}
        </span>
        <div class="line"></div>
      </div>
      <div class="part-c-img-and-info">
        <BaseImage
          :key="partCProposition.propositionId"
          :url="partCProposition.residences[0].image"
          :is-placeholder-small="true"
          class="part-c-img"
        />
        <div class="part-c-info">
          <div class="street-address part-c">
            {{ partCProposition.residences[0].streetAddress }}
            <template v-if="partCProposition.residences.length > 1">
              & {{ partCProposition.residences[1].streetAddress }}
            </template>
            <BaseIcon
              v-if="partCProposition.verified"
              icon-file="verified-white-check"
              :width="12"
              :height="12"
              class="verified-badge"
            />
          </div>
          <p class="locality">
            {{ partCProposition.residences[0].locality }}
            <template v-if="partCProposition.residences.length > 1">
              & {{ partCProposition.residences[1].locality }}
            </template>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { truncateText } from '@/utils/helpers/string.js';
import { todayYesterdayDate } from '@/utils/helpers/date.js';
import { convertSystemChatMessageWithoutAddress } from '@/utils/systemMessageConverter';

export default {
  name: 'MatchCard',

  props: {
    match: {
      type: Object,
      required: true
    },
    selectedMatch: {
      type: Object,
      default: null
    }
  },

  computed: {
    ...mapGetters({
      activeTab: 'matches/activeTab'
    }),

    partBProposition() {
      return this.match.propositions[0];
    },

    partCProposition() {
      return this.match.propositions[1] || null;
    },

    partBPropositionActive() {
      return this.partBProposition.inactivatedAt === null;
    },

    isSelected() {
      return this.selectedMatch?.id === this.match?.id;
    },

    swapIsInactive() {
      let isInactivated = false;

      if (this.match.lastMessage?.senderName === 'SYSTEM') {
        const parsedContent = JSON.parse(this.match.lastMessage?.message);
        isInactivated = parsedContent.type === 'INTEREST_LEVEL_3';
      }

      return this.activeTab === 'inactive' || isInactivated;
    },

    expireDays() {
      if (!this.match.expireAt) {
        return null;
      }

      const expireInDays = this.$dayjs(this.match.expireAt).diff(
        this.$dayjs(),
        'day'
      );

      if (expireInDays < 0) {
        return null;
      } else if (expireInDays <= 1) {
        return this.$t('match_expire_day_left', {
          days: expireInDays
        });
      } else if (expireInDays <= 3) {
        return this.$t('match_expire_days_left', {
          days: expireInDays
        });
      }

      return null;
    }
  },

  methods: {
    customDate: todayYesterdayDate,
    truncateText,
    convertSystemChatMessageWithoutAddress
  }
};
</script>

<style scoped lang="scss">
.match-card {
  position: relative;
  background: #ffffff;
  color: #2c3e50;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0px 4px 8px -2px rgba(16, 24, 40, 0.15),
    0px 2px 4px -2px rgba(16, 24, 40, 0.2);
  margin-bottom: 16px;
  user-select: none;
  cursor: pointer;

  @media ($desktop) {
    border: 2.5px solid transparent;
  }

  &.isSelected {
    border-color: $main-blue;
  }
}

.primary-image {
  width: 100%;
  object-fit: cover;
  aspect-ratio: 2 / 0.9;
}

.badge {
  position: absolute;
  top: 10px;
  right: 12px;
  display: flex;
  padding: 3px 10px 3px 8px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 16px;
  background-color: #fff;
  font-size: 12px;
  font-weight: 600;
  color: #007aff;
  z-index: 1;

  .new-match-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: #007aff;
  }

  &.expire-match-badge {
    color: #ff8c00;
    background-color: #fff4e0;
  }
}

.last-message {
  position: absolute;
  top: 10px;
  right: 10px;
  max-width: 80%;
  border-radius: 20px;
  color: #2c3e50;
  padding: 14px;
  padding-right: 20px;
  background: linear-gradient(
    287deg,
    rgba(255, 240, 240, 0.8) -0.52%,
    rgba(255, 255, 255, 0.8) 100%
  );
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(3px);

  .has-badge & {
    top: 20px;
    padding-right: 50px;
  }

  .is-inactive & {
    background-color: #fce4e4;
  }

  .name-and-date {
    color: #2c3e50;
    opacity: 0.9;
    font-size: 12px;
    line-height: 1;
    font-weight: 700;
    margin-bottom: 5px;
  }

  .message {
    color: #2c3e50;
    font-size: 12px;
    line-height: 1;
  }

  .message-system:first-letter {
    text-transform: capitalize;
  }
}

.residence-info-container {
  position: relative;
  padding: 11px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  @media ($medium-screen) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  @media ($large-screen) {
    flex-direction: row;
    align-items: center;
    gap: 0px;
  }
}

.residence-info {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.adress-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.street-address {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 16px;

  &.part-c {
    font-size: 13px;
    margin-bottom: 1px;
  }
}

.locality {
  font-weight: 600;
  font-size: 12px;
  color: #485261;
  margin: 0;
}

.verified-badge {
  margin-left: 5px;
  margin-top: -2px;
}

.residence-info-details {
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  margin-top: 4px;
}

.link-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.go-to-proposition {
  background-color: #f0f5fa;
  color: #2c3e50;
  font-size: 13px;
  font-weight: 600;
  border: none;
  border-radius: 20px;
  padding: 8px 12px;
  white-space: nowrap;

  &:hover,
  &:focus {
    background-color: #e2e8ee;
  }
}

.part-c-divider {
  display: flex;
  align-items: center;
  padding: 5px 11px 0 11px;
}

.part-c-divider .line {
  flex: 1;
  border-top: 1px solid #dddcdb;
}

.part-c-divider .text {
  margin: 0 16px;
  font-size: 12px;
  color: #707070;
}

.part-c-img-and-info {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 11px 8px 11px;
}

.part-c-img {
  width: 46px;
  aspect-ratio: 1/1;
  object-fit: cover;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.2);
}

.part-c-info {
  width: 100%;
}
</style>
